import { ArrowRight } from "lucide-react";
import { CoachIcon } from "@/components/CoachIcon";
import { TierDot } from "@/components/school/TierDot";
import type { SchoolTier } from "@/services/pipeline";
import { coachSuggestion } from "./pipelineDesign";

const TIERS: Array<{ id: SchoolTier; label: string; bar: string }> = [
  { id: "dream", label: "Dream", bar: "bg-amber" },
  { id: "realistic", label: "Realistic", bar: "bg-primary" },
  { id: "safety", label: "Safety", bar: "bg-safety" },
];

interface ListBalanceProps {
  total: number;
  counts: Record<SchoolTier, number>;
  /** Hide the coach suggestion while assessments are still updating. */
  showSuggestion: boolean;
  onPrompt: (prompt: string, threadTitle: string) => void;
}

/** The list's Dream / Realistic / Safety balance, with one coach suggestion based on it. */
export function ListBalance({ total, counts, showSuggestion, onPrompt }: ListBalanceProps) {
  const assessed = counts.dream + counts.realistic + counts.safety;
  const unassessed = Math.max(0, total - assessed);
  const suggestion = coachSuggestion(counts);

  return (
    <section
      aria-label="List balance"
      className="mt-4 flex flex-col gap-4 rounded-2xl border border-border bg-white p-4 lg:flex-row lg:items-center lg:gap-6"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-bold text-forest">
            Your list: {total} school{total === 1 ? "" : "s"}
          </span>
          {unassessed > 0 && (
            <span className="text-xs text-subtle">{unassessed} still being assessed</span>
          )}
        </div>
        <div className="mt-2 flex h-2.5 gap-[3px] overflow-hidden rounded-full bg-muted">
          {TIERS.filter(tier => counts[tier.id] > 0).map(tier => (
            <span
              key={tier.id}
              className={tier.bar}
              style={{ flexGrow: counts[tier.id], flexBasis: 0 }}
            />
          ))}
          {unassessed > 0 && (
            <span className="bg-border-strong" style={{ flexGrow: unassessed, flexBasis: 0 }} />
          )}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-forest-soft">
          {TIERS.map(tier => (
            <span key={tier.id} className="inline-flex items-center gap-1.5">
              <TierDot tier={tier.id} />
              <strong className="tabular-nums text-forest">{counts[tier.id]}</strong> {tier.label}
            </span>
          ))}
        </div>
      </div>

      {showSuggestion && (
        <>
          <div aria-hidden="true" className="hidden w-px self-stretch bg-border lg:block" />
          <div className="flex items-center gap-3 lg:max-w-[380px]">
            <span
              aria-hidden="true"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-forest text-primary"
            >
              <CoachIcon className="h-6 w-6" />
            </span>
            <p className="text-[13px] leading-snug text-forest-soft">
              <span className="font-semibold text-forest">{suggestion.message}</span>{" "}
              <button
                type="button"
                onClick={() => onPrompt(suggestion.prompt, suggestion.threadTitle)}
                className="inline-flex items-center gap-1 font-bold text-primary-text hover:underline"
              >
                {suggestion.action} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </p>
          </div>
        </>
      )}
    </section>
  );
}
